import { useState } from 'react';
import { useAuthGuard } from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import client from '../api/client';

export default function PlaceCompetitor() {
  const checkAuth = useAuthGuard();
  const [urls, setUrls] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);

  const handleAnalyze = async () => {
    if (!checkAuth()) return;
    const urlList = urls.split('\n').map(u => u.trim()).filter(Boolean);
    if (!urlList.length) return alert('URL을 입력해주세요');
    setLoading(true);
    try {
      const res = await client.post('/place/competitor/analyze', { urls: urlList });
      setResults(res.data.results);
      setExpanded(null);
    } catch { alert('분석 실패'); }
    finally { setLoading(false); }
  };

  const s: any = {
    card: { background: '#161B27', border: '0.5px solid #2A3045', borderRadius: 8, padding: 20, marginBottom: 12 },
    input: { width: '100%', background: '#0F1117', border: '0.5px solid #2A3045', borderRadius: 8, padding: '10px 14px', color: '#E8ECF4', fontSize: 13, boxSizing: 'border-box' },
    btn: { background: '#00D4AA', border: 'none', borderRadius: 8, padding: '10px 28px', color: '#0A1F18', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
    tag: (color: string) => ({ background: color + '22', border: `1px solid ${color}55`, borderRadius: 4, padding: '2px 8px', color, fontSize: 11 }),
    section: { marginBottom: 16 },
    sectionTitle: { color: '#B0BAC8', fontSize: 12, fontWeight: 600, marginBottom: 8 },
  };

  return (
    <Layout>
      <div style={{ maxWidth: 900 }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ color: '#E8ECF4', fontSize: 18, fontWeight: 700 }}>플레이스 경쟁사 분석</div>
          <div style={{ color: '#8892A4', fontSize: 12, marginTop: 4 }}>네이버 플레이스 URL을 입력하면 경쟁사 정보를 분석합니다 (최대 5개)</div>
        </div>

        <div style={s.card}>
          <label style={{ color: '#B0BAC8', fontSize: 12, marginBottom: 6, display: 'block' }}>플레이스 URL (줄바꿈으로 구분)</label>
          <textarea style={{ ...s.input, height: 120, resize: 'vertical', marginBottom: 16 }}
            value={urls} onChange={e => setUrls(e.target.value)}
            placeholder={"https://map.naver.com/p/entry/place/1952498498\nhttps://naver.me/xxxx"} />
          <button style={s.btn} onClick={handleAnalyze} disabled={loading}>
            {loading ? '분석 중...' : '분석 시작'}
          </button>
        </div>

        {results.map((r, i) => (
          <div key={i} style={s.card}>
            {r.error ? (
              <div>
                <div style={{ color: '#8892A4', fontSize: 11, marginBottom: 4 }}>{r.url}</div>
                <div style={{ color: '#F56565' }}>⚠️ {r.error}</div>
              </div>
            ) : (
              <>
                {/* 헤더 */}
                <div style={{ display: 'flex', gap: 14, marginBottom: 16, cursor: 'pointer' }} onClick={() => setExpanded(expanded === i ? null : i)}>
                  {r.thumbnail && <img src={r.thumbnail} style={{ width: 72, height: 72, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />}
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#E8ECF4', fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{r.name}</div>
                    <div style={{ color: '#8892A4', fontSize: 12, marginBottom: 4 }}>{r.category} · {r.address}</div>
                    {r.address_jibun && <div style={{ color: '#4A5568', fontSize: 11, marginBottom: 4 }}>지번: {r.address_jibun}</div>}
                    {r.micro_reviews?.length > 0 && (
                      <div style={{ color: '#00D4AA', fontSize: 11, marginTop: 2 }}>💬 {r.micro_reviews[0]}</div>
                    )}
                    {r.phone && <div style={{ color: '#8892A4', fontSize: 12 }}>📞 {r.phone}</div>}

                  </div>
                  <span style={{ color: '#4A5568', fontSize: 12 }}>{expanded === i ? '▲' : '▼'}</span>
                </div>

                {/* 핵심 지표 */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
                  <div style={{ background: '#0F1117', borderRadius: 8, padding: '12px 14px', textAlign: 'center' }}>
                    <div style={{ color: '#4A5568', fontSize: 10, marginBottom: 4 }}>방문자 리뷰</div>
                    <div style={{ color: '#00D4AA', fontSize: 16, fontWeight: 700 }}>{typeof r.visitor_review_total === 'number' ? r.visitor_review_total.toLocaleString() : (r.visitor_review_total || '-')}</div>
                    <div style={{ color: '#4A5568', fontSize: 10 }}>평점 {r.visitor_review_score || '-'}</div>
                  </div>
                  <div style={{ background: '#0F1117', borderRadius: 8, padding: '12px 14px', textAlign: 'center' }}>
                    <div style={{ color: '#4A5568', fontSize: 10, marginBottom: 4 }}>블로그 리뷰</div>
                    <div style={{ color: '#378ADD', fontSize: 16, fontWeight: 700 }}>{r.blog_review_total ? r.blog_review_total.toLocaleString() : '-'}</div>
                  </div>
                  <div style={{ background: '#0F1117', borderRadius: 8, padding: '12px 14px', textAlign: 'center' }}>
                    <div style={{ color: '#4A5568', fontSize: 10, marginBottom: 4 }}>서비스</div>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap', marginTop: 4 }}>
                      {r.booking && <span style={s.tag('#00D4AA')}>예약</span>}
                      {r.delivery && <span style={s.tag('#378ADD')}>배달</span>}
                      {r.order && <span style={s.tag('#F6AD55')}>주문</span>}
                      {r.npay && <span style={s.tag('#00D4AA')}>N페이</span>}
                    </div>
                  </div>
                </div>

                {/* 상세 정보 */}
                {expanded === i && (
                  <div>
                    {/* 소개 */}
                    {r.description && (
                      <div style={s.section}>
                        <div style={s.sectionTitle}>📝 소개</div>
                        <div style={{ background: '#0F1117', borderRadius: 6, padding: '12px 14px', color: '#8892A4', fontSize: 12, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{r.description}</div>
                      </div>
                    )}

                    {/* 찾아가는 길 */}
                    {r.road && (
                      <div style={s.section}>
                        <div style={s.sectionTitle}>🗺️ 찾아가는 길</div>
                        <div style={{ background: '#0F1117', borderRadius: 6, padding: '12px 14px', color: '#8892A4', fontSize: 12, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{r.road}</div>
                      </div>
                    )}

                    {/* 편의시설 */}
                    {r.conveniences?.length > 0 && (
                      <div style={s.section}>
                        <div style={s.sectionTitle}>🏪 편의시설 ({r.conveniences.length}개)</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {r.conveniences.map((c: string) => (
                            <span key={c} style={{ background: '#1A2035', borderRadius: 4, padding: '3px 8px', color: '#8892A4', fontSize: 11 }}>{c}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 결제수단 */}
                    {r.payment?.length > 0 && (
                      <div style={s.section}>
                        <div style={s.sectionTitle}>💳 결제수단</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {r.payment.map((p: string) => (
                            <span key={p} style={{ background: '#1A2035', borderRadius: 4, padding: '3px 8px', color: '#8892A4', fontSize: 11 }}>{p}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 온라인 채널 */}
                    <div style={s.section}>
                      <div style={s.sectionTitle}>🔗 온라인 채널</div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {r.instagram && (
                          <a href={r.instagram} target="_blank" rel="noreferrer"
                            style={{ background: '#1A2035', borderRadius: 6, padding: '6px 14px', color: '#E8ECF4', fontSize: 12, textDecoration: 'none' }}>
                            📸 인스타그램
                          </a>
                        )}
                        {r.homepages?.map((h: any, hi: number) => (
                          <a key={hi} href={h.url} target="_blank" rel="noreferrer"
                            style={{ background: '#1A2035', borderRadius: 6, padding: '6px 14px', color: '#E8ECF4', fontSize: 12, textDecoration: 'none' }}>
                            🌐 {h.type || '홈페이지'}
                          </a>
                        ))}
                        {r.blog_id && (
                          <a href={`https://blog.naver.com/${r.blog_id}`} target="_blank" rel="noreferrer"
                            style={{ background: '#1A2035', borderRadius: 6, padding: '6px 14px', color: '#E8ECF4', fontSize: 12, textDecoration: 'none' }}>
                            ✍️ 네이버 블로그
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </Layout>
  );
}
